import "@tento-ui/component-builtin";
import "@tento-ui/components/icons/link-icon";

import { LonaWebComponent, html } from "@tento-ui/component";
import { component } from "@tento-ui/component-decorators";
import { css } from "@tento-ui/component-styles";
import { ClockIcon, LonaIcons } from "@tento-ui/ui/icons";
import { LonaLinkIcon } from "@tento-ui/components/icons/link-icon";
import { $makeNuxCtaRow } from "./cta-row";
import { $makeNuxTextListCell } from "./text-list-cell";

/**
 * Onboarding tip card: scheduling links.
 *
 * A booking-link preview and a "Create a link" CTA, both rendered as
 * `std-list-cell-layout` rows for a unified look. CTA opens the scheduling-link
 * sidebar (`onCreate` → `showSchedulingLink`).
 */
@component({ name: "std-nux-scheduling-card" })
export class NuxSchedulingCard extends LonaWebComponent {
  private $previewAnchor: HTMLElement = this.$("preview-anchor");
  private $ctaAnchor: HTMLElement = this.$("cta-anchor");
  private onCreate: Option<() => void> = null;

  static makeWith(callbacks: NuxSchedulingCard.Callbacks): NuxSchedulingCard {
    return NuxSchedulingCard.make().bind(callbacks);
  }

  bind(callbacks: NuxSchedulingCard.Callbacks): this {
    this.onCreate = callbacks.onCreate;

    this.$previewAnchor.replaceChildren(this.$preview());
    this.$ctaAnchor.replaceChildren(
      $makeNuxCtaRow({
        $icon: LonaLinkIcon.make(),
        label: "Create a link",
        onClick: () => this.onCreate?.(),
      }),
    );

    return this;
  }

  // Booking-link preview, rendered as a list cell to match the CTA.
  // Illustrative only — not interactive (the CTA below does the work).
  private $preview(): HTMLElement {
    return $makeNuxTextListCell({
      title: "30 min meeting",
      caption: "lona.so/you/30min",
      $icon: LonaIcons.make(ClockIcon, { sizePx: 16, color: "var(--text-sec)" }),
    });
  }

  static $styles = [
    css`
      :host {
        display: block;
        height: 100%;
      }

      #container {
        height: 100%;
        padding: 16px;
        justify-content: center;
        gap: 14px;
      }

      #blurb {
        color: var(--text-sec);
        font-size: var(--text-12px);
        line-height: 1.4;
      }
    `,
  ];

  static $html = html`
    <std-col id="container">
      <std-col id="preview-anchor"></std-col>
      <span id="blurb">Share a link so others can book time on your calendar.</span>
      <std-col id="cta-anchor"></std-col>
    </std-col>
  `;
}

export namespace NuxSchedulingCard {
  export type Callbacks = {
    onCreate: () => void;
  };
}
